import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Car, Shield, Scale, Award, AlertCircle, FileText, ChevronRight, DollarSign, BookOpen, ArrowRight, Clock, Calculator, MapPin, Target, AlertOctagon } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import RelatedPracticeAreas from '@/components/RelatedPracticeAreas';
import PracticeAreaFAQ from '@/components/PracticeAreaFAQ';
import PageVideoSection from '@/components/PageVideoSection';

// Car crash related videos
const carCrashVideos = [
  {
    id: 'sYmMMFu5M5Y',
    title: "What to Do After a Car Crash",
    description: "Step-by-step guide from an attorney on protecting your rights after an accident.",
    duration: "4:44"
  },
  {
    id: 'V6TMxEZgJnc',
    title: "PIP and UIM Insurance Explained",
    description: "Understanding Personal Injury Protection and Uninsured Motorist coverage.",
    duration: "4:20"
  },
  {
    id: '5ge73GHqeSo',
    title: "How an Attorney Helps After a Crash",
    description: "What to expect when you hire a lawyer for your motor vehicle collision claim.",
    duration: "2:38"
  },
  {
    id: '7ryJH_KzmUw',
    title: "Who's Involved in a Collision Claim?",
    description: "Understanding the parties and insurance companies in your claim.",
    duration: "1:19"
  }
];

const carCrashFAQs = [
  {
    question: "How long do I have to file a car accident claim in Washington?",
    answer: "Washington has a 3-year statute of limitations for personal injury claims (RCW 4.16.080). However, I recommend contacting an attorney immediately—evidence disappears quickly, and early action strengthens your case significantly."
  },
  {
    question: "What if the other driver doesn't have insurance?",
    answer: "You may still have options through your own Uninsured/Underinsured Motorist (UIM) coverage, which is required in Washington. I can help you understand your policy and pursue all available compensation."
  },
  {
    question: "Should I talk to the other driver's insurance company?",
    answer: "I strongly advise against giving recorded statements to the other driver's insurance company before speaking with an attorney. They may use your words against you to reduce or deny your claim. Let me handle those conversations for you."
  },
  {
    question: "What compensation can I recover after a car accident?",
    answer: "You may be entitled to medical expenses (past and future), lost wages, pain and suffering, property damage, and in some cases, diminished earning capacity. The exact amount depends on your injuries and circumstances."
  },
  {
    question: "What if I was partially at fault for the accident?",
    answer: "Washington follows 'pure comparative negligence,' meaning you can still recover damages even if you were partially at fault. Your compensation is reduced by your percentage of fault—if you're 20% at fault, you recover 80% of damages."
  },
  {
    question: "Do I really need a lawyer for my car accident case?",
    answer: "Studies show that injured people who hire attorneys typically recover 3-5 times more than those who don't, even after attorney fees. I handle all negotiations, paperwork, and legal strategy so you can focus on healing."
  }
];

const CarCrashesPage = () => {
  // HowTo schema for "Steps After a Car Accident"
  const howToSteps = {
    name: "What To Do After a Car Accident in Washington State",
    description: "Step-by-step guide to protect your rights and build a strong case after a car crash in Washington.",
    totalTime: "PT30M",
    steps: [
      { name: "Ensure Safety", text: "Move to a safe location if possible. Call 911 if anyone is injured." },
      { name: "Document the Scene", text: "Take photos of vehicles, damage, road conditions, traffic signs, and any visible injuries." },
      { name: "Exchange Information", text: "Get contact and insurance information from all drivers involved, plus names and numbers of witnesses." },
      { name: "File a Police Report", text: "Request that officers respond to document the accident. Get the report number for your records." },
      { name: "Seek Medical Attention", text: "See a doctor immediately, even if you feel okay. Some injuries don't show symptoms right away." },
      { name: "Don't Admit Fault", text: "Avoid saying 'I'm sorry' or admitting fault. Stick to the facts when speaking with police and others." },
      { name: "Contact an Attorney Before Insurance", text: "Speak with a personal injury attorney before giving recorded statements to any insurance company." }
    ]
  };

  return (
    <div className="min-h-screen pt-20" data-testid="car-crashes-page">
      <SEOHead 
        title="Car Accident Attorney | Washington State | Amy C. Brown Law"
        description="Experienced car accident lawyer in Washington State. Helping victims of auto collisions recover compensation for injuries, lost wages, and pain and suffering. Free consultation."
        serviceData={{
          name: "Car Accident Legal Representation",
          description: "Legal representation for victims of automobile accidents in Washington State, including multi-vehicle collisions, rear-end accidents, and uninsured motorist claims.",
          category: "Personal Injury Law"
        }}
        howToData={howToSteps}
      />
      {/* Hero Section */}
      <section className="pt-36 pb-16 md:pt-40 md:pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-6">
              Car Crashes
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              After a car crash, the stress of dealing with insurance companies and medical treatment can feel overwhelming. Whether your accident happened in Everett, Lynnwood, Marysville, Mukilteo, or anywhere in Washington State, I have deep experience in injury claims and can step in to ease this burden. Having worked on both defense and plaintiff sides, I know how insurance companies operate.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold hover:bg-primary/90 transition-all duration-300"
                data-testid="car-hero-call"
              >
                <Phone className="w-5 h-5" />
                Call (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold hover:bg-secondary hover:text-white transition-all duration-300"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Your Situation */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
                Insurance companies calling, medical providers billing, nothing but questions.
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                I've been there myself. I've helped countless clients in your position.
              </p>
              <div className="bg-accent-soft rounded-2xl p-8 border-l-4 border-teal-500">
                <p className="text-slate-700 leading-relaxed">
                  At Amy C Brown Law, I'm more than an attorney—I've experienced trauma myself. Multiple members of my family were killed in a construction-related incident. I vividly remember that horrible day and the days after. I know pain and I understand the pain my clients feel.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-slate-50 rounded-2xl p-6 hover-lift">
                <Car className="w-10 h-10 text-teal-600 mb-4" />
                <h4 className="font-semibold text-secondary mb-2">Deep Experience</h4>
                <p className="text-sm text-slate-600">Years of handling car accident injury claims in Washington State</p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 hover-lift">
                <Shield className="w-10 h-10 text-teal-600 mb-4" />
                <h4 className="font-semibold text-secondary mb-2">Insurance Insight</h4>
                <p className="text-sm text-slate-600">Former defense attorney—I know their tactics</p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 hover-lift">
                <Scale className="w-10 h-10 text-teal-600 mb-4" />
                <h4 className="font-semibold text-secondary mb-2">No Fees Unless You Win</h4>
                <p className="text-sm text-slate-600 mb-2">Contingency fee basis means no upfront costs</p>
                <Link to="/pricing" className="inline-flex items-center gap-1 text-teal-600 text-sm font-medium hover:text-teal-700">
                  See pricing <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 hover-lift">
                <Award className="w-10 h-10 text-teal-600 mb-4" />
                <h4 className="font-semibold text-secondary mb-2">Proven Results</h4>
                <p className="text-sm text-slate-600">Many cases settled for policy limits</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Car Accident Resources - Educational Content Hub */}
      <section className="py-16 bg-slate-50" data-testid="car-accident-resources-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mb-4">
              Car Accident Resources
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              I've put together these guides to help you understand what happens after an accident. Whether you're dealing with insurance calls, wondering about your case value, or unsure if you need a lawyer—these resources can help.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link 
              to="/what-to-do-after-car-accident" 
              className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-primary/30 hover:shadow-lg transition-all"
            >
              <FileText className="w-8 h-8 text-primary mb-3" />
              <h3 className="font-heading font-semibold text-secondary mb-2 group-hover:text-primary transition-colors">
                What To Do After a Car Accident
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                A step-by-step guide covering everything from the accident scene to dealing with insurance—including the mistakes to avoid.
              </p>
              <span className="inline-flex items-center gap-1 text-primary font-medium text-sm">
                Read the guide <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            
            <Link 
              to="/should-i-talk-to-insurance" 
              className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-primary/30 hover:shadow-lg transition-all"
            >
              <Shield className="w-8 h-8 text-primary mb-3" />
              <h3 className="font-heading font-semibold text-secondary mb-2 group-hover:text-primary transition-colors">
                Should I Talk to the Insurance Company?
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Why insurance companies ask for recorded statements—and what I learned from years on their side about how they use them.
              </p>
              <span className="inline-flex items-center gap-1 text-primary font-medium text-sm">
                Learn more <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            
            <Link 
              to="/should-i-accept-settlement" 
              className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-primary/30 hover:shadow-lg transition-all"
            >
              <DollarSign className="w-8 h-8 text-primary mb-3" />
              <h3 className="font-heading font-semibold text-secondary mb-2 group-hover:text-primary transition-colors">
                Should I Accept the Settlement Offer?
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                How to recognize a low offer, what early settlements usually leave out, and what research shows about case values.
              </p>
              <span className="inline-flex items-center gap-1 text-primary font-medium text-sm">
                Read more <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            
            <Link 
              to="/how-much-is-my-case-worth" 
              className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-primary/30 hover:shadow-lg transition-all"
            >
              <Calculator className="w-8 h-8 text-primary mb-3" />
              <h3 className="font-heading font-semibold text-secondary mb-2 group-hover:text-primary transition-colors">
                How Much Is My Case Worth?
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                The factors that determine case value, what research shows about attorney representation, and a link to our case value estimator.
              </p>
              <span className="inline-flex items-center gap-1 text-primary font-medium text-sm">
                Explore factors <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            
            <Link 
              to="/how-long-does-settlement-take" 
              className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-primary/30 hover:shadow-lg transition-all"
            >
              <Clock className="w-8 h-8 text-primary mb-3" />
              <h3 className="font-heading font-semibold text-secondary mb-2 group-hover:text-primary transition-colors">
                How Long Does a Settlement Take?
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                The typical timeline from accident to resolution, the five phases of a case, and what affects how long yours might take.
              </p>
              <span className="inline-flex items-center gap-1 text-primary font-medium text-sm">
                See timeline <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            
            <Link 
              to="/when-to-hire-car-accident-lawyer" 
              className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-primary/30 hover:shadow-lg transition-all"
            >
              <Scale className="w-8 h-8 text-primary mb-3" />
              <h3 className="font-heading font-semibold text-secondary mb-2 group-hover:text-primary transition-colors">
                When Should I Hire a Lawyer?
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Signs you'd benefit from legal help, situations where you might be okay on your own, and honest guidance either way.
              </p>
              <span className="inline-flex items-center gap-1 text-primary font-medium text-sm">
                Find out <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Personal Injury Info */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
              Personal Injury
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Personal injury law enables individuals injured by another's negligence or intentional actions to seek compensation for damages. It covers incidents like car accidents, maritime injuries, or other harm, addressing medical expenses, lost wages, and pain and suffering.
            </p>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">
                What You May Be Entitled To:
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-teal-600 text-sm">✓</span>
                  </span>
                  <span className="text-slate-700">Medical expenses (current and future)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-teal-600 text-sm">✓</span>
                  </span>
                  <span className="text-slate-700">Lost wages and loss of earning capacity</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-teal-600 text-sm">✓</span>
                  </span>
                  <span className="text-slate-700">Pain and suffering</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-teal-600 text-sm">✓</span>
                  </span>
                  <span className="text-slate-700">Property damage</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Related Practice Areas */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl font-bold text-secondary text-center mb-12">
            Related Practice Areas
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Link to="/first-responder-crashes" className="group bg-slate-50 rounded-2xl p-8 hover-lift border border-slate-100">
              <Shield className="w-10 h-10 text-teal-600 mb-4" />
              <h3 className="font-heading text-xl font-semibold text-secondary mb-2 group-hover:text-teal-600 transition-colors">
                First Responder Crashes
              </h3>
              <p className="text-slate-600 mb-4">
                Peace officers can pursue civil claims against at-fault parties, including criminals and their insurers.
              </p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium">
                Learn more <ChevronRight className="w-4 h-4" />
              </span>
            </Link>

            <Link to="/motorcycle-crashes" className="group bg-slate-50 rounded-2xl p-8 hover-lift border border-slate-100">
              <Car className="w-10 h-10 text-teal-600 mb-4" />
              <h3 className="font-heading text-xl font-semibold text-secondary mb-2 group-hover:text-teal-600 transition-colors">
                Motorcycle Crashes
              </h3>
              <p className="text-slate-600 mb-4">
                Motorcycle accidents can result in severe injuries. I fight for maximum compensation.
              </p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium">
                Learn more <ChevronRight className="w-4 h-4" />
              </span>
            </Link>

            <Link to="/slip-and-falls" className="group bg-slate-50 rounded-2xl p-8 hover-lift border border-slate-100">
              <AlertCircle className="w-10 h-10 text-teal-600 mb-4" />
              <h3 className="font-heading text-xl font-semibold text-secondary mb-2 group-hover:text-teal-600 transition-colors">
                Slip and Falls
              </h3>
              <p className="text-slate-600 mb-4">
                Protect your rights and prove liability after a slip and fall accident.
              </p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium">
                Learn more <ChevronRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Specialized Car Accident Pages - Internal Linking for SEO */}
      <section className="py-16 bg-slate-50" data-testid="specialized-car-accident-pages">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mb-3">
              Specialized Car Accident Pages
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Looking for information specific to your situation? Browse our dedicated pages for different types of car accident cases.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link 
              to="/everett-car-accident-lawyer" 
              className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all"
            >
              <MapPin className="w-8 h-8 text-teal-600 mb-3" />
              <h3 className="font-heading font-semibold text-secondary mb-2 group-hover:text-teal-600 transition-colors">
                Everett Car Accident Lawyer
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Local representation for car accident victims in Everett, WA.
              </p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm">
                Learn more <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            
            <Link 
              to="/snohomish-county-car-accident-lawyer" 
              className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all"
            >
              <MapPin className="w-8 h-8 text-teal-600 mb-3" />
              <h3 className="font-heading font-semibold text-secondary mb-2 group-hover:text-teal-600 transition-colors">
                Snohomish County Car Accident
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Serving all of Snohomish County: Everett, Marysville, Lake Stevens & more.
              </p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm">
                Learn more <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            
            <Link 
              to="/rear-end-accident-lawyer" 
              className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all"
            >
              <Target className="w-8 h-8 text-teal-600 mb-3" />
              <h3 className="font-heading font-semibold text-secondary mb-2 group-hover:text-teal-600 transition-colors">
                Rear-End Accident Lawyer
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Whiplash, back injuries, and soft tissue damage from rear-end collisions.
              </p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm">
                Learn more <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            
            <Link 
              to="/drunk-driver-accident-lawyer" 
              className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all"
            >
              <AlertOctagon className="w-8 h-8 text-teal-600 mb-3" />
              <h3 className="font-heading font-semibold text-secondary mb-2 group-hover:text-teal-600 transition-colors">
                Drunk Driver Accident Lawyer
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                DUI crash injuries with potential punitive damages.
              </p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm">
                Learn more <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Case Examples */}
      <section className="py-20 bg-accent-soft">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl font-bold text-secondary text-center mb-12">
            Case Examples
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <Award className="w-10 h-10 text-teal-600 mb-4" />
              <h3 className="font-heading text-xl font-semibold text-secondary mb-2">
                Workers Injured While Driving
              </h3>
              <p className="text-slate-600">
                I successfully settled a claim for a worker injured on the job by a drunk driver, in which there were significant gaps in medical treatment.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <Award className="w-10 h-10 text-teal-600 mb-4" />
              <h3 className="font-heading text-xl font-semibold text-secondary mb-2">
                Injuries with Preexisting Conditions
              </h3>
              <p className="text-slate-600">
                I won arbitration of a rear end collision against a major auto insurer with significant preexisting injury.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Statute of Limitations */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl mx-auto text-center">
            <AlertCircle className="w-16 h-16 text-teal-600 mx-auto mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
              Take Timely Action to Protect Your Case
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Do not let precious time slip away—acting swiftly is critical to protect your case. Evidence can vanish, and memories of witnesses may fade, jeopardizing your opportunity for a positive resolution for your case. With a <a href="https://apps.leg.wa.gov/RCW/default.aspx?cite=4.16.080" target="_blank" rel="noopener noreferrer" className="text-teal-600 underline hover:text-teal-800 font-medium">three-year statute of limitations</a> for most personal injury claims in Washington, I urge you to contact me today where my compassion and expertise can help develop a strategy to win your case and provide you clarity and calm.
            </p>
          </div>
        </div>
      </section>

      {/* Helpful Resources - Internal Linking */}
      <section className="py-16 bg-slate-50" data-testid="car-resources-section">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <BookOpen className="w-4 h-4" />
              Learn More
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mb-3">
              Helpful Resources for Car Accident Victims
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Understanding your rights after a car crash is the first step toward fair compensation.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Link
              to="/blog/what-to-do-after-car-accident-washington"
              className="group bg-white hover:bg-teal-50 rounded-xl p-6 border border-slate-200 hover:border-teal-300 transition-all duration-300 shadow-sm"
            >
              <FileText className="w-10 h-10 text-teal-600 mb-4" />
              <h3 className="font-heading font-bold text-secondary mb-2 group-hover:text-teal-700 transition-colors">
                What to Do After a Car Accident
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Step-by-step guide to protect your rights and build a strong case from day one.
              </p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm group-hover:gap-2 transition-all">
                Read Guide <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            
            <Link
              to="/blog/why-statutes-of-limitations-exist-washington-personal-injury"
              className="group bg-white hover:bg-teal-50 rounded-xl p-6 border border-slate-200 hover:border-teal-300 transition-all duration-300 shadow-sm"
            >
              <Clock className="w-10 h-10 text-teal-600 mb-4" />
              <h3 className="font-heading font-bold text-secondary mb-2 group-hover:text-teal-700 transition-colors">
                How Long Do I Have to File?
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Washington's 3-year statute of limitations explained—and important exceptions you need to know.
              </p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm group-hover:gap-2 transition-all">
                Learn More <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            
            <Link
              to="/compensation-estimator"
              className="group bg-white hover:bg-teal-50 rounded-xl p-6 border border-slate-200 hover:border-teal-300 transition-all duration-300 shadow-sm"
            >
              <Calculator className="w-10 h-10 text-teal-600 mb-4" />
              <h3 className="font-heading font-bold text-secondary mb-2 group-hover:text-teal-700 transition-colors">
                What Is My Case Worth?
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Use our free calculator to get an estimate of your potential compensation.
              </p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm group-hover:gap-2 transition-all">
                Try Calculator <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <PracticeAreaFAQ
        title="Car Accident FAQs"
        subtitle="Answers to common questions about auto accident claims in Washington State."
        faqs={carCrashFAQs}
        accentColor="primary"
        schemaId="car-crashes"
      />

      {/* Video Resources Section */}
      <PageVideoSection
        videos={carCrashVideos}
        title="Car Accident Videos"
        subtitle="Watch these videos to learn what to do after a crash and how I can help."
        theme="rose"
      />

      {/* Related Practice Areas */}
      <RelatedPracticeAreas currentSlug="car-crashes" />

      {/* CTA */}
      <section className="py-20 bg-teal-600 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Free Consultation - No Obligation
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            I personally respond to every inquiry within 1 business day.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-white text-teal-700 px-10 py-5 rounded-full font-bold text-xl hover:bg-teal-50 transition-all duration-300 shadow-xl"
              data-testid="car-cta-call"
            >
              <Phone className="w-6 h-6" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-10 py-5 rounded-full font-bold text-xl hover:bg-white/10 transition-all duration-300"
            >
              <MessageCircle className="w-6 h-6" />
              Text (425) 675-1224
            </a>
          </div>
          <p className="mt-6 text-teal-200 text-sm">
            Page last updated: December 2025
          </p>
        </div>
      </section>
    </div>
  );
};

export default CarCrashesPage;
